import z from "zod";

export const CompanyIdTypeSchema = z.enum(
	[
		"bn", // Canada - Business Number
		"gst", // Canada/India/Australia - GST Number
		"ein", // US - Employer Identification Number
		"vat", // EU/UK - VAT Number
		"abn", // Australia - Australian Business Number
		"other", // Fallback for unlisted countries
	],
	{
		error: "Invalid company identifier type",
	},
);
export type CompanyIdType = z.infer<typeof CompanyIdTypeSchema>;

/**
 * Validation patterns for company ID values based on type.
 * Format requirements are sourced from Stripe's Tax ID documentation.
 */
export const COMPANY_ID_PATTERNS: Record<
	CompanyIdType,
	{ pattern: RegExp; message: string }
> = {
	ein: {
		pattern: /^\d{2}-?\d{7}$/,
		message: "EIN must be 9 digits (format: XX-XXXXXXX or XXXXXXXXX)",
	},
	bn: {
		pattern: /^\d{9}$/,
		message: "Business Number must be exactly 9 digits",
	},
	gst: {
		pattern: /^\d{9}RT\d{4}$/i,
		message:
			"GST/HST number must be 9 digits + RT + 4 digits (e.g., 123456789RT0001)",
	},
	abn: {
		pattern: /^\d{11}$/,
		message: "ABN must be exactly 11 digits",
	},
	vat: {
		pattern: /^[A-Z]{2}[A-Z0-9]{7,13}$/i,
		message:
			"VAT number must start with 2-letter country code followed by 7-13 alphanumeric characters",
	},
	other: {
		pattern: /^.+$/,
		message: "Please provide a company identifier",
	},
};

/**
 * Validates a company ID value against the expected format for its type.
 * Returns { valid: true } if the value passes, or { valid: false, message: string } if not.
 */
export function validateCompanyIdValue(
	type: CompanyIdType | undefined,
	value: string | undefined,
): { valid: boolean; message?: string } {
	// If either is missing, validation passes (handled by withConditionalGroups)
	if (!type || !value) return { valid: true };

	const rule = COMPANY_ID_PATTERNS[type];
	if (!rule.pattern.test(value)) {
		return { valid: false, message: rule.message };
	}
	return { valid: true };
}

/**
 * Zod refinement for company ID validation.
 * Apply to schemas that have both companyIdType and companyIdValue fields.
 *
 * @example
 * const schema = withCompanyIdValidation(z.object({
 *   companyIdType: CompanyIdTypeSchema.optional(),
 *   companyIdValue: z.string().optional(),
 * }));
 */
export function withCompanyIdValidation<T extends z.ZodTypeAny>(schema: T) {
	return schema.superRefine((data: unknown, ctx) => {
		const obj = data as {
			companyIdType?: CompanyIdType;
			companyIdValue?: string;
		};
		const result = validateCompanyIdValue(
			obj.companyIdType,
			obj.companyIdValue,
		);
		if (!result.valid) {
			ctx.addIssue({
				code: "custom",
				message: result.message,
				path: ["companyIdValue"],
			});
		}
	});
}

/** Company data fields extracted from signup request */
export const SignupCompanyDataSchema = withCompanyIdValidation(
	z.object({
		country: z.string().length(2).optional(),
		companyName: z.string().optional(),
		companyIdType: CompanyIdTypeSchema.optional(),
		companyIdValue: z.string().optional(),
	}),
);
export type SignupCompanyData = z.infer<typeof SignupCompanyDataSchema>;

/** Full request body for /sign-up/email endpoint */
export const SignupEmailBodySchema = withCompanyIdValidation(
	z.object({
		email: z.email(),
		password: z.string().min(1),
		name: z.string().min(1),
		callbackURL: z.string().optional(),
		captchaToken: z.string().optional(),
		// Company fields (will be stripped before Better Auth validation)
		country: z.string().length(2).optional(),
		companyName: z.string().optional(),
		companyIdType: CompanyIdTypeSchema.optional(),
		companyIdValue: z.string().optional(),
	}),
);
export type SignupEmailBody = z.infer<typeof SignupEmailBodySchema>;

/** User returned from Better Auth after signup (ctx.context.returned.user) */
export const BetterAuthUserSchema = z.object({
	id: z.string(),
	email: z.email(),
	name: z.string().optional(),
	emailVerified: z.boolean(),
	image: z.string().nullable(),
	role: z.enum(["user", "admin", "super_admin"]),
	banned: z.boolean(),
	banReason: z.string().nullable(),
	banExpires: z.number().nullable(),
	twoFactorEnabled: z.boolean(),
	stripeCustomerId: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type BetterAuthUser = z.infer<typeof BetterAuthUserSchema>;

/** Schema for ctx.context.returned from Better Auth */
export const BetterAuthReturnedSchema = z.object({
	token: z.string().nullable(),
	user: BetterAuthUserSchema.optional(),
});
export type BetterAuthReturned = z.infer<typeof BetterAuthReturnedSchema>;
